import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  WrapperDiv,
  RowWithBottomBorderDiv,
  StyledWrapper,
  StyledLabel,
  ReferenceAndResourcesButton,
  StyledANCCStatementWrapper,
} from "./_styled";
import ReferencesAndResourcesDialog from "../ReferencesAndResourcesDialog";
import AboutTabRow from "../AboutTabRow";
import AboutTabContributorRow from "../AboutTabContributorRow";
import AboutTabEndorsersRow from "../AboutTabEndorsersRow";
import { Type, ListItem } from "@ebscois/react-component-library";

const AboutTab = ({ about, isDesktop }) => {
  const MONTHS_ARRAY = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const lastUpdatedDate = about?.lastUpdatedDate?.value;
  const publicationDate = about?.publicationDate?.value;

  let label;
  let dateValue;
  if (lastUpdatedDate || publicationDate) {
    label = lastUpdatedDate
      ? about.lastUpdatedDate.label
      : about.publicationDate.label;
    const date = lastUpdatedDate
      ? new Date(lastUpdatedDate)
      : new Date(publicationDate);
    dateValue = `${
      MONTHS_ARRAY[date.getUTCMonth()]
    }-${date.getUTCDate()}-${date.getUTCFullYear()}`;
  }
  const [showReferencesAndResources, setShowReferencesAndResources] = useState(
    false
  );
  const sections = about?.referencesAndResources?.sections || [];
  return (
    <>
      {about && (
        <WrapperDiv data-testid={about.testId}>
          {(lastUpdatedDate || publicationDate) && (
            <StyledWrapper data-testid="reviewAndUpdatedDate-row">
              <StyledLabel
                element="h6"
                data-testid="reviewAndUpdatedDate-label"
                fontWeight="semibold"
                textTransform="uppercase"
                color="secondary"
              >
                {label}
              </StyledLabel>
              <Type fontSize="lg" data-testid="reviewAndUpdatedDate-value">
                {dateValue}
              </Type>
            </StyledWrapper>
          )}
          {about?.authors?.values && (
            <AboutTabRow
              label={about.authors.label}
              value={buildContributors(about.authors)}
              id="authors-row"
            />
          )}
          {about?.editors?.values && (
            <AboutTabRow
              label={about.editors.label}
              value={buildContributors(about.editors)}
              id="editors-row"
            />
          )}
          {about?.reviewers?.values && (
            <AboutTabRow
              label={about.reviewers.label}
              value={buildContributors(about.reviewers)}
              id="reviewers-row"
            />
          )}
          {about?.endorsers?.length > 0 &&
            about.endorsers.map((endorser) => (
              <AboutTabEndorsersRow endorser={endorser} />
            ))}
          {about?.contributors?.length > 0 &&
            about.contributors.map((data) => (
              <AboutTabContributorRow data={data} />
            ))}
          {sections.length > 0 && (
            <>
              <RowWithBottomBorderDiv />
              <AboutTabRow
                value={buildReferencesAndResourcesDialog(
                  setShowReferencesAndResources,
                  about.referencesAndResources.title
                )}
                id="references-row"
              />
            </>
          )}
          {about?.anccStatement && (
            <StyledWrapper data-testid="ancc-statement-row">
              <StyledLabel
                element="h6"
                data-testid="ancc-statement-label"
                fontWeight="bold"
                color="secondary"
              >
                {about.anccStatement.label}
              </StyledLabel>
              <StyledANCCStatementWrapper type="ul">
                {about.anccStatement.values.map((statement) => (
                  <ListItem key={statement} data-testid="ancc-statement-item">
                    <span dangerouslySetInnerHTML={{ __html: statement }} />
                  </ListItem>
                ))}
              </StyledANCCStatementWrapper>
            </StyledWrapper>
          )}
        </WrapperDiv>
      )}
      <ReferencesAndResourcesDialog
        showModal={showReferencesAndResources}
        setModalVisibility={setShowReferencesAndResources}
        referenceAndResources={about.referencesAndResources}
      />
    </>
  );
};

const buildContributors = (contributors) => {
  return contributors.values[0].data
    ? contributors.values.map((contributor, index) => (
        <div key={`${contributor.data}-${index}`}>{contributor.data}</div>
      ))
    : contributors.values.map((contributor, index) => (
        <div key={`${contributor.givenName}-${contributor.surname}-${index}`}>
          {contributor.givenName} {contributor.surname}
          {contributor.degrees && <>, {contributor.degrees}</>}
        </div>
      ));
};

const buildReferencesAndResourcesDialog = (
  setShowReferencesAndResources,
  linkName
) => {
  return (
    <ReferenceAndResourcesButton
      kind="anchor"
      variant="primary"
      onClick={() => {
        setShowReferencesAndResources(true);
      }}
      data-testid="referneces-and-resource-link"
    >
      {linkName}
    </ReferenceAndResourcesButton>
  );
};

AboutTab.propTypes = {
  about: PropTypes.shape({
    authors: PropTypes.shape({
      label: PropTypes.string,
      values: PropTypes.array,
    }),
    editors: PropTypes.shape({
      label: PropTypes.string,
      values: PropTypes.array,
    }),
    publicationDate: PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
    testId: PropTypes.string,
  }),
};

AboutTab.displayName = "AboutTab";
export default React.memo(AboutTab);
